import { useState, useEffect, useCallback } from 'react';
import promoConfig from '../config/promoConfig.json';

const STORAGE_PREFIX = 'duoacolor_promo_';

/**
 * Hook para gestionar la logica del modal promocional
 * - Control de frecuencia (configurable, por defecto 1 vez al dia)
 * - Validacion de rango de fechas de campana
 * - Persistencia en localStorage
 */
export const usePromoModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [config] = useState(promoConfig);

    /**
     * Obtiene la clave de localStorage para esta promo
     */
    const getStorageKey = useCallback(() => {
        return `${STORAGE_PREFIX}${config.id}_lastShown`;
    }, [config.id]);

    /**
     * Verifica si la promo esta dentro del rango de fechas
     */
    const isWithinDateRange = useCallback(() => {
        const now = new Date();
        const start = config.startDate ? new Date(config.startDate) : null;
        const end = config.endDate ? new Date(config.endDate) : null;

        if (start && now < start) return false;
        if (end && now > end) return false;

        return true;
    }, [config.startDate, config.endDate]);

    /**
     * Verifica si ha pasado suficiente tiempo desde la ultima vez
     */
    const hasFrequencyElapsed = useCallback(() => {
        try {
            const lastShown = localStorage.getItem(getStorageKey());

            if (!lastShown) return true;

            const lastShownTime = parseInt(lastShown, 10);
            const now = Date.now();
            const frequencyMs = config.frequencyHours * 60 * 60 * 1000;

            return (now - lastShownTime) >= frequencyMs;
        } catch {
            return true;
        }
    }, [config.frequencyHours, getStorageKey]);

    /**
     * Registra que se mostro el modal
     */
    const recordShown = useCallback(() => {
        try {
            localStorage.setItem(getStorageKey(), Date.now().toString());
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('Error saving promo state:', error);
            }
        }
    }, [getStorageKey]);

    /**
     * Determina si el modal debe mostrarse
     */
    const shouldShowModal = useCallback(() => {
        if (!config.enabled) return false;
        if (!isWithinDateRange()) return false;
        if (!hasFrequencyElapsed()) return false;

        return true;
    }, [config.enabled, isWithinDateRange, hasFrequencyElapsed]);

    /**
     * Efecto para mostrar el modal al montar (con delay para UX)
     */
    useEffect(() => {
        if (shouldShowModal()) {
            const timer = setTimeout(() => {
                setIsOpen(true);
                recordShown();
            }, 1500);

            return () => clearTimeout(timer);
        }
    }, [shouldShowModal, recordShown]);

    /**
     * Cierra el modal
     */
    const closeModal = useCallback(() => {
        setIsOpen(false);
    }, []);

    /**
     * Abre el modal manualmente (para testing/preview)
     */
    const openModal = useCallback(() => {
        setIsOpen(true);
    }, []);

    /**
     * Resetea el estado de la promo (para testing)
     */
    const resetPromoState = useCallback(() => {
        try {
            localStorage.removeItem(getStorageKey());
        } catch {
            // Silenciar errores
        }
    }, [getStorageKey]);

    return {
        isOpen,
        config,
        closeModal,
        openModal,
        resetPromoState,
        shouldShowModal: shouldShowModal(),
        isWithinDateRange: isWithinDateRange()
    };
};

export default usePromoModal;
